import {createContext, FC, useContext, useMemo, useState} from "react";
import {TeamProgressWidget} from "@/enums";
import {SocketContext} from "./socketContext";
import {useDownloadReport} from "@/pages/ManagerView/Pages/TeamProgress/hooks/useDownloadReport";

export type ExportType = keyof typeof TeamProgressWidget

export interface ExportTeamProgressContextValue {
	exportQueue: ExportType[];
	addExportTask: (exportTaskName: ExportType) => void;
	removeExportTask: (exportTaskName: ExportType) => void;
	exportTaskExists: (exportTaskName: ExportType) => boolean;
	exportTasksRunning: () => boolean;
}

const ExportTeamProgressContext = createContext<ExportTeamProgressContextValue | undefined>(undefined);

export const useExportTeamProgressContext = () => {
	const context = useContext(ExportTeamProgressContext);
	if (!context) {
		throw new Error("useExportTeamProgressContext must be used within a ExportTeamProgressProvider");
	}
	return context;
}

interface ExportTeamProgressProviderProps {
	children: React.ReactNode;
}

export const ExportTeamProgressProvider: FC<ExportTeamProgressProviderProps> = ({children}) => {
	const socket = useContext(SocketContext)
	const {endExportBackendProcess} = useDownloadReport();
	const [exportQueue, setExportQueue] = useState<ExportType[]>([]);

	const handleExportTeamProgressData = (e: { data: { isSuccessful: boolean; reportType: string; fileName: string; }}) => {
		const {isSuccessful, reportType, fileName} = e.data;
		endExportBackendProcess(isSuccessful, fileName);
		removeFromExportQueue(reportType as ExportType);
	}

	if (socket) {
		socket.on("connect", () => {
			if (socket.listeners("sendTeamProgressDataForReports").length === 0){
				socket.on("sendTeamProgressDataForReports", handleExportTeamProgressData);
			}
		});
	}

	const addToExportQueue = (exportTaskName: ExportType) => {
		if (exportQueue.includes(exportTaskName)){
			return;
		}
		setExportQueue([...exportQueue, exportTaskName]);
	}

	const removeFromExportQueue = (exportTaskName: ExportType) => {
		setExportQueue(previous => previous.filter(task => task !== exportTaskName));
	}

	const isTaskInExportQueue = (exportTaskName: ExportType) => {
		return exportQueue.includes(exportTaskName);
	}

	const areThereAnyTasksInExportQueue = () => {
		return exportQueue.length > 0;
	}

	const contextValue = useMemo(() => ({
		exportQueue,
		addExportTask: addToExportQueue,
		removeExportTask: removeFromExportQueue,
		exportTaskExists: isTaskInExportQueue,
		exportTasksRunning: areThereAnyTasksInExportQueue
	}), [exportQueue])

	return (
		<ExportTeamProgressContext.Provider value={contextValue}>
			{children}
		</ExportTeamProgressContext.Provider>
	);
}